import { useEffect, useState } from "react";
import { X } from "@phosphor-icons/react";
import { Dialog as RadixDialog } from "radix-ui";
import { AnimatePresence, motion } from "motion/react";
import { Link } from "wouter";
import { PLANS, type Plan } from "../../shared/types.ts";
import { Bar } from "../landing/shapes.tsx";
import { Button, cn, softSpring, useToast } from "../ui/index.ts";
import { useDesktop } from "../ui/useMedia.ts";
import { ERROR_TEXT } from "../lib/api.ts";
import { num } from "../lib/format.ts";
import type { Paid } from "../lib/plan.ts";
import { above, useCheckout } from "./settings/Upgrade.tsx";

export type Limit = "plan_limit" | "daily_cap";

const EVENT = "headcount:limit";

export const isLimit = (code: string | null | undefined): code is Limit => code === "plan_limit" || code === "daily_cap";

/** Tells the app an apply was refused for a plan limit, so it offers the plans above the user's. */
export const askUpgrade = (limit: Limit) => dispatchEvent(new CustomEvent<Limit>(EVENT, { detail: limit }));

/**
 * What opens when an apply is refused for a limit: a centered modal on desktop, a bottom sheet on phones, with the plans
 * above `plan` and a checkout for the cheapest. A user with no plan above theirs gets the limit's toast instead, and one
 * whose plan hasn't loaded gets it with a link to the plans, so a paid user is never offered a plan they are past.
 */
export function UpgradeModal({ plan: known }: { plan: Plan | null }) {
  const plan = known ?? "max";
  const { toast } = useToast();
  const desktop = useDesktop();
  const [limit, setLimit] = useState<Limit | null>(null);
  const [pick, setPick] = useState<Paid>("autopilot");
  const { busy, go } = useCheckout();
  const upgrades = above(plan);

  useEffect(() => {
    const on = (e: Event) => {
      const l = (e as CustomEvent<Limit>).detail;
      if (!upgrades.length)
        return void toast(l === "daily_cap" ? ERROR_TEXT.daily_cap! : "You've used this month's applications.", "bad", known ? undefined : { href: "/upgrade", label: "Plans" });
      setPick(upgrades[0]!);
      setLimit(l);
    };
    addEventListener(EVENT, on);
    return () => removeEventListener(EVENT, on);
  }, [plan, known]);

  const daily = limit === "daily_cap";
  const top = PLANS[upgrades[upgrades.length - 1] ?? "max"];
  const title = daily ? "You've hit today's limit." : plan === "free" ? "You've used this month's free applications." : `You've used this month's ${num(PLANS[plan].monthly)} applications.`;
  const line = daily ? `${upgrades.length > 1 ? "Paid plans send" : `${top.name} sends`} up to ${num(top.daily)} a day.` : "Upgrade and keep applying today.";
  const shown = desktop ? { opacity: 1, y: 0, scale: 1 } : { y: 0 };
  const hidden = desktop ? { opacity: 0, y: 12, scale: 0.97 } : { y: "100%" };

  return (
    <RadixDialog.Root open={limit !== null} onOpenChange={(o) => !o && setLimit(null)}>
      <AnimatePresence>
        {limit ? (
          <RadixDialog.Portal forceMount>
            <RadixDialog.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-accent/30 backdrop-blur-[2px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              />
            </RadixDialog.Overlay>
            <div className={cn("pointer-events-none fixed inset-0 z-50 flex justify-center", desktop ? "items-center p-4" : "items-end")}>
              <RadixDialog.Content asChild forceMount>
                <motion.div
                  className={cn(
                    "pointer-events-auto w-full overflow-hidden bg-surface text-ink shadow-raised outline-none",
                    desktop ? "max-w-[460px] rounded-xl" : "rounded-t-xl pb-[env(safe-area-inset-bottom)]",
                  )}
                  initial={hidden}
                  animate={shown}
                  exit={hidden}
                  transition={softSpring}
                >
                  <div className="relative overflow-hidden bg-primary px-6 pb-6 pt-7 text-on-primary">
                    <Bar tone="orange" delay={0.15} className="-bottom-5 right-7 h-[96px] w-[34px]" />
                    <Bar tone="tint" flip delay={0.25} opacity={0.22} className="-bottom-9 right-[62px] h-[84px] w-[34px]" />
                    <RadixDialog.Close asChild>
                      <motion.button
                        type="button"
                        aria-label="Close"
                        whileTap={{ scale: 0.9 }}
                        className="absolute right-3 top-3 grid size-8 place-items-center rounded-control text-on-primary/80 transition-[background-color,color] duration-150 hover:bg-white/15 hover:text-on-primary"
                      >
                        <X size={16} weight="bold" aria-hidden />
                      </motion.button>
                    </RadixDialog.Close>
                    <RadixDialog.Title className="relative pr-10 font-display text-[24px] leading-tight text-balance">{title}</RadixDialog.Title>
                    <RadixDialog.Description className="relative mt-2 text-[15px] text-on-primary/80">{line}</RadixDialog.Description>
                  </div>
                  <div role="radiogroup" aria-label="Plans" className="flex flex-col gap-2 p-5 pb-0">
                    {upgrades.map((p) => {
                      const on = p === pick;
                      return (
                        <button
                          key={p}
                          type="button"
                          role="radio"
                          aria-checked={on}
                          onClick={() => setPick(p)}
                          className={cn(
                            "flex items-center gap-3.5 rounded-control px-4 py-3.5 text-left transition-[background-color,box-shadow] duration-150",
                            on ? "bg-primary-soft ring-2 ring-primary ring-inset" : "ring-1 ring-line-strong ring-inset hover:bg-sunk",
                          )}
                        >
                          <span aria-hidden className={cn("grid size-5 shrink-0 place-items-center rounded-full ring-2 ring-inset transition-[box-shadow] duration-150", on ? "ring-primary" : "ring-line-strong")}>
                            <motion.span className="size-2.5 rounded-full bg-primary" initial={false} animate={{ scale: on ? 1 : 0 }} transition={softSpring} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-[15px] font-bold text-ink">{PLANS[p].name}</span>
                            <span className="mt-0.5 block text-[13px] text-muted tabular-nums">
                              {daily ? `${num(PLANS[p].daily)} applications a day` : `${num(PLANS[p].monthly)} applications a month`}
                            </span>
                          </span>
                          <span className="shrink-0 text-ink tabular-nums">
                            <span className="font-display text-[22px] font-extrabold tracking-[-0.03em]">${PLANS[p].price}</span>
                            <span className="text-[13px] text-muted">/month</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex flex-col items-center gap-1 p-5">
                    <Button size="lg" className="w-full" loading={busy === pick} disabled={busy !== null} onClick={() => go(pick)}>
                      Upgrade to {PLANS[pick].name}
                    </Button>
                    <Link
                      href="/upgrade"
                      onClick={() => setLimit(null)}
                      className="rounded-control px-3 py-2 text-[13.5px] font-semibold text-primary-ink underline-offset-2 hover:underline"
                    >
                      Compare plans
                    </Link>
                  </div>
                </motion.div>
              </RadixDialog.Content>
            </div>
          </RadixDialog.Portal>
        ) : null}
      </AnimatePresence>
    </RadixDialog.Root>
  );
}
